import React from 'react';
import { SpinnerIos16Regular, SpinnerIos20Regular } from '@fluentui/react-icons';
import styles from './Transition.module.css';
import { FIELD_TARGETS, SHEET_PURPOSES, WIZARD_STEPS } from './constants';
import { UploadIcon } from './icons';

const STEP_DETAIL = ['Upload', 'Worksheets', 'Match fields', 'Review changes', 'Finish'];

function formatBytes(bytes) {
    const value = Number(bytes) || 0;
    if (value < 1024) return `${value} bytes`;
    if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function fileBadge(filename, fileType) {
    const name = (filename || '').toLowerCase();
    if (name.endsWith('.csv') || fileType === 'csv') return 'CSV';
    return 'XLS';
}

function parseStatusLabel(status) {
    const key = String(status || 'pending').toLowerCase();
    if (key === 'ready') return 'Ready';
    if (key === 'processing') return 'Processing…';
    if (key === 'failed') return 'Failed';
    if (key === 'pending') return 'Pending…';
    return status || 'Pending…';
}

function fileIsProcessing(file) {
    const key = String(file?.parse_status || 'pending').toLowerCase();
    return key === 'pending' || key === 'processing';
}

function ProcessingBanner({ message }) {
    return (
        <div className={styles.processingBanner} role="status" aria-live="polite">
            <SpinnerIos20Regular className={`rotateLoader ${styles.processingSpinner}`} aria-hidden="true" />
            <span>{message}</span>
        </div>
    );
}

function purposeLabel(kind) {
    return SHEET_PURPOSES.find(([value]) => value === kind)?.[1] || 'Other / not sure';
}

function sheetRoleLabel(sheet) {
    const role = sheet?.sheet_role;
    if (role === 'tracker_data') return 'Tracker data';
    if (role === 'overview_meta') return 'Overview / instructions';
    if (role === 'lookup_lists') return 'Lookup lists';
    if (role === 'reference_export') return 'Reference export';
    if (role === 'risk_register') return 'Risk register';
    if (role === 'large_export') return 'Large export';
    if (role === 'ambiguous') return 'Review suggested';
    return null;
}

function sheetSelectionHelp(sheet) {
    if (sheet?.selection_reason) return sheet.selection_reason;
    const supporting = sheet?.kind === 'supporting';
    if (supporting && !sheet?.selected) return 'Stored for reference and excluded from completion counts.';
    return 'Confirm the purpose matches how you use this worksheet.';
}

function columnTarget(column) {
    return column?.target || column?.suggestion?.proposed_field || 'custom';
}

function sheetReviewState(sheet) {
    if (sheet.needs_header_review && !sheet.header_confirmed) return 'review';
    return 'ready';
}

function statusRawsForSheet(sheet, statusSamples) {
    return [
        ...new Set(
            (statusSamples || [])
                .filter(sample => sample.sheet_id === sheet.id)
                .map(sample => sample.raw)
                .filter(Boolean),
        ),
    ];
}

function sheetHasAmbiguousDates(sheet, dateSamples) {
    return (dateSamples || []).some(sample => sample.sheet_id === sheet.id && sample.suggestion?.ambiguous);
}

function mappingState(column, sheet, statusRules, statusSamples, dateConvention, dateSamples) {
    const target = columnTarget(column);
    if (target === 'custom') return 'custom';
    if (target === 'status') {
        let raws = statusRawsForSheet(sheet, statusSamples);
        if (!raws.length) raws = (column.samples || []).map(sample => String(sample || '').trim()).filter(Boolean);
        if (!raws.length) return 'review';
        if (raws.some(raw => !statusRules?.[raw]?.confirmed)) return 'review';
        return 'matched';
    }
    if (target === 'target_date') {
        if (sheetHasAmbiguousDates(sheet, dateSamples) && !dateConvention) return 'review';
    }
    return 'matched';
}

function matchSummary(sheet, statusRules, statusSamples, dateConvention, dateSamples) {
    let matched = 0;
    let review = 0;
    let custom = 0;
    (sheet?.columns || []).forEach(column => {
        const state = mappingState(column, sheet, statusRules, statusSamples, dateConvention, dateSamples);
        if (state === 'matched') matched += 1;
        else if (state === 'review') review += 1;
        else custom += 1;
    });
    return { matched, review, custom };
}

function sheetMatchLabel(sheet, statusRules, statusSamples, dateConvention, dateSamples) {
    const columns = sheet.columns || [];
    if (!columns.length) return 'Review needed';
    const states = columns.map(column => mappingState(column, sheet, statusRules, statusSamples, dateConvention, dateSamples));
    if (states.some(state => state === 'review')) return 'Review needed';
    if (states.every(state => state === 'matched' || state === 'custom')) return 'Matched';
    return 'Matched';
}

function statusRulesForSheet(sheet, statusRules, statusSamples) {
    const raws = new Set(statusRawsForSheet(sheet, statusSamples));
    (sheet?.columns || []).forEach(column => {
        if (columnTarget(column) !== 'status') return;
        (column.samples || []).forEach(sample => {
            const raw = String(sample || '').trim();
            if (raw) raws.add(raw);
        });
    });
    return [...raws].sort().map(raw => [raw, statusRules?.[raw] || { canonical: 'UNKNOWN', confirmed: false }]);
}

export default function Wizard({
    wizard,
    onClose,
    onSaveDraft,
    onFile,
    onRemoveFile,
    onStep,
    onToggleSheet,
    onPurpose,
    onMatchSheet,
    onMapColumn,
    onStatusRule,
    onDateConvention,
    onReviewed,
    onImport,
    onOpenOverview,
    onOpenTracker,
    onViewDetails,
    onUpdateMode,
    onReviewRow,
    onPreviewSheet,
    onChooseHeaderRow,
    aiMappingEnabled = false,
    onAiMapping,
}) {
    const step = wizard.step;
    const inputRef = React.useRef(null);
    const statusRef = React.useRef(null);
    const dateRef = React.useRef(null);
    const sheets = (wizard.files || []).flatMap(file => (file.worksheets || []).map(sheet => ({ ...sheet, filename: file.filename, snapshotId: file.id })));
    const selectedSheets = sheets.filter(sheet => sheet.selected);
    const selectedCount = selectedSheets.length;
    const title =
        step === 2
            ? 'Choose which worksheets to import'
            : step === 3
              ? 'Check how your columns match'
              : step === 4
                ? 'Review what Lens will import'
                : step === 5
                  ? 'Your tracker is ready'
                  : 'Import your existing tracker';
    const uploadReady =
        (wizard.files || []).length > 0 &&
        (wizard.files || []).some(file => String(file.parse_status || '').toLowerCase() === 'ready');
    const updateSourceOk = !wizard.hasRecords || wizard.updateChoice !== 'update' || Boolean(wizard.sourceId);
    const canContinue =
        step === 1
            ? uploadReady && updateSourceOk
            : step === 2
              ? selectedCount > 0
              : step === 4
                ? wizard.reviewed
                : true;
    const parsing = (wizard.files || []).some(file => {
        const status = String(file.parse_status || '').toLowerCase();
        return status === 'pending' || status === 'processing';
    });
    const parseFailed = (wizard.files || []).some(file => String(file.parse_status || '').toLowerCase() === 'failed');
    const footNote =
        wizard.commitMessage ||
        (step === 1
            ? !updateSourceOk
                ? 'Choose an existing source to update before you upload.'
                : parseFailed
                  ? 'Fix or remove the failed file above, then upload again.'
                  : parsing
                    ? 'Parsing worksheets… Continue unlocks when at least one file is ready.'
                    : (wizard.files || []).length
                      ? 'Nothing is written until you import.'
                      : 'Add at least one readable file to continue.'
            : step === 2
              ? `${selectedCount} selected · ${selectedSheets.filter(sheet => sheetReviewState(sheet) === 'ready').length} ready · ${selectedSheets.filter(sheet => sheetReviewState(sheet) === 'review').length} need a quick review`
              : step === 3
                ? 'Selections are preserved when you go back.'
                : step === 4 && !wizard.reviewed
                  ? "Confirm that you've reviewed the proposed changes."
                  : 'Nothing is written until you import.');
    const continueLabel = step === 2 ? 'Continue to match fields' : step === 3 ? 'Review changes' : step === 4 ? 'Import records' : 'Continue';

    return (
        <div className={styles.wizard}>
            <div className={styles.wizardHead}>
                <div className={styles.wizardHeadInner}>
                    <div className={styles.wizardTitleRow}>
                        <div>
                            <div className={styles.wizardTitleLine}>
                                <h2 className={styles.wizardTitle}>{title}</h2>
                            </div>
                            <p className={styles.wizardStepLabel}>
                                Step {step} of 5 · {STEP_DETAIL[step - 1]}
                            </p>
                        </div>
                        <div className={styles.wizardActions}>
                            {step < 5 ? (
                                <button type="button" className={styles.linkBtn} onClick={onSaveDraft} disabled={wizard.busy}>
                                    Save draft
                                </button>
                            ) : null}
                            <button type="button" className={styles.mutedBtn} onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                    <div className={styles.stepRail}>
                        {WIZARD_STEPS.map((name, index) => {
                            const number = index + 1;
                            const on = number <= step;
                            const current = number === step;
                            return (
                                <div className={styles.step} key={name}>
                                    <div className={on ? styles.stepBarOn : styles.stepBar} />
                                    <p className={current ? styles.stepNameOn : styles.stepName}>
                                        {number} {name}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className={styles.wizardBody}>
                <div className={styles.wizardInner}>
                    {wizard.error ? <p className={styles.warn}>{wizard.error}</p> : null}
                    {step === 1 && (wizard.busy || parsing) ? (
                        <ProcessingBanner
                            message={
                                wizard.busy && !(wizard.files || []).some(fileIsProcessing)
                                    ? 'Uploading your file…'
                                    : 'Reading worksheets — this can take a minute for large workbooks.'
                            }
                        />
                    ) : null}
                    {step === 1 ? (
                        <UploadStep wizard={wizard} inputRef={inputRef} onFile={onFile} onRemoveFile={onRemoveFile} onUpdateMode={onUpdateMode} />
                    ) : null}
                    {step === 2 ? (
                        <SheetStep sheets={sheets} onToggleSheet={onToggleSheet} onPurpose={onPurpose} onPreviewSheet={onPreviewSheet} onChooseHeaderRow={onChooseHeaderRow} />
                    ) : null}
                    {step === 3 && wizard.aiMappingMessage ? <p className={styles.finePrint}>{wizard.aiMappingMessage}</p> : null}
                    {step === 3 ? (
                    <MatchStep
                        sheets={selectedSheets}
                        matchSheetId={wizard.matchSheetId}
                        statusRules={wizard.statusRules}
                        statusSamples={wizard.statusSamples}
                        dateSamples={wizard.dateSamples}
                        dateConvention={wizard.dateConvention}
                        aiMappingEnabled={aiMappingEnabled}
                        aiBusy={wizard.busy}
                        onAiMapping={onAiMapping}
                        onMatchSheet={onMatchSheet}
                        onMapColumn={onMapColumn}
                        onStatusRule={onStatusRule}
                        onDateConvention={onDateConvention}
                        statusRef={statusRef}
                        dateRef={dateRef}
                    />
                    ) : null}
                    {step === 4 ? (
                        <ReviewStep wizard={wizard} sheets={selectedSheets} onReviewed={onReviewed} onGoToMatch={() => onStep(3)} />
                    ) : null}
                    {step === 5 ? <FinishStep wizard={wizard} result={wizard.result} onOpenOverview={onOpenOverview} onOpenTracker={onOpenTracker} onViewDetails={onViewDetails} /> : null}
                </div>
            </div>
            {step === 5 ? null : (
                <div className={styles.wizardFoot}>
                    <button type="button" className={styles.ghostBtn} disabled={step === 1 || wizard.busy} onClick={() => onStep(step - 1)}>
                        Back
                    </button>
                    <span className={styles.footNote}>{footNote}</span>
                    <div className={styles.footSpacer}>
                        <button type="button" className={styles.primaryBtn} disabled={!canContinue || wizard.busy || (step === 1 && parsing)} onClick={() => (step === 4 ? onImport() : onStep(step + 1))}>
                            {wizard.busy || (step === 1 && parsing) ? (
                                <>
                                    <SpinnerIos16Regular className={`rotateLoader ${styles.btnSpinner}`} aria-hidden="true" />
                                    <span>{step === 1 ? 'Processing…' : 'Working…'}</span>
                                </>
                            ) : (
                                continueLabel
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function UploadStep({ wizard, inputRef, onFile, onRemoveFile, onUpdateMode }) {
    const onDrop = event => {
        event.preventDefault();
        if (wizard.busy) return;
        const file = event.dataTransfer?.files?.[0];
        if (file) onFile(file);
    };
    const selectedSource = (wizard.sources || []).find(source => String(source.id) === String(wizard.sourceId));
    const uploadLocked = wizard.busy;

    return (
        <div className={uploadLocked ? styles.uploadStepBusy : undefined}>
            {wizard.hasRecords ? (
                <>
                    <h3 className={styles.sectionTitle}>Update your tracker without losing changes made in Lens</h3>
                    <p className={styles.sectionCopy}>Choose how this upload should relate to your existing tracker.</p>
                    <div className={styles.choiceGrid}>
                        <button type="button" className={wizard.updateChoice === 'update' ? styles.choiceOn : styles.choice} onClick={() => onUpdateMode('update')}>
                            <p className={styles.choiceTitle}>Update an existing tracker</p>
                            <p className={styles.choiceCopy}>Recommended when refreshing the same source file.</p>
                        </button>
                        <button type="button" className={wizard.updateChoice === 'add' ? styles.choiceOn : styles.choice} onClick={() => onUpdateMode('add')}>
                            <p className={styles.choiceTitle}>Add another spreadsheet</p>
                            <p className={styles.choiceCopy}>Keep prior imports and merge in a new file.</p>
                        </button>
                    </div>
                    {wizard.updateChoice === 'update' ? (
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>Existing source</span>
                            <select className={styles.select} aria-label="Existing source" value={wizard.sourceId || ''} onChange={event => onUpdateMode('update', event.target.value)}>
                                <option value="">Choose a previous import</option>
                                {(wizard.sources || []).map(source => (
                                    <option key={source.id} value={source.id}>
                                        {source.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : (
                        <p className={styles.finePrint}>Adding a new source file. Existing tracker rows and prior imports stay as they are.</p>
                    )}
                    {wizard.updateChoice === 'update' && selectedSource ? (
                        <p className={styles.sheetMeta}>Updating source: {selectedSource.label}</p>
                    ) : null}
                </>
            ) : null}
            {(wizard.files || []).length ? (
                <div className={styles.uploadList}>
                    {(wizard.files || []).map(file => {
                        const processing = fileIsProcessing(file);
                        return (
                            <div key={file.id} className={processing ? `${styles.fileChip} ${styles.fileChipProcessing}` : styles.fileChip}>
                                <div className={styles.fileMark}>{fileBadge(file.filename, file.file_type)}</div>
                                <div className={styles.fileChipMain}>
                                    <p className={styles.fileName}>{file.filename}</p>
                                    <p className={styles.fileMeta}>
                                        {file.file_type || 'file'} · {formatBytes(file.byte_size)} · {(file.worksheets || []).length} worksheets ·{' '}
                                        <span className={styles.fileStatus}>
                                            {processing ? (
                                                <SpinnerIos16Regular className={`rotateLoader ${styles.fileSpinner}`} aria-hidden="true" />
                                            ) : null}
                                            {parseStatusLabel(file.parse_status)}
                                        </span>
                                    </p>
                                    {file.parse_error ? <p className={styles.warn}>{file.parse_error}</p> : null}
                                </div>
                                <button type="button" className={styles.linkBtn} disabled={uploadLocked} onClick={() => onRemoveFile(file.id)}>
                                    Remove
                                </button>
                            </div>
                        );
                    })}
                </div>
            ) : null}
            <div
                className={uploadLocked ? `${styles.dropzone} ${styles.dropzoneBusy}` : styles.dropzone}
                onDragOver={event => event.preventDefault()}
                onDrop={onDrop}
                role="presentation">
                {uploadLocked ? (
                    <div className={styles.dropzoneOverlay} aria-hidden="true">
                        <SpinnerIos20Regular className={`rotateLoader ${styles.processingSpinner}`} />
                    </div>
                ) : null}
                <UploadIcon />
                <p className={styles.dropTitle}>Drop an Excel or CSV file here</p>
                <p className={styles.dropCopy}>Import Excel or CSV. Multiple sheets are fine. Macros are not run.</p>
                <input
                    ref={inputRef}
                    className={styles.hiddenFile}
                    type="file"
                    accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    disabled={uploadLocked}
                    onChange={event => event.target.files?.[0] && onFile(event.target.files[0])}
                />
                <button type="button" className={styles.primaryBtn} disabled={uploadLocked} onClick={() => inputRef.current?.click()}>
                    Browse files
                </button>
            </div>
            <p className={styles.finePrint}>
                You can import multiple worksheets and files in one go. You will choose what to include next. Original files and source metadata are preserved; macros and workbook code are not executed.
            </p>
            {(wizard.files || []).length ? (
                <button type="button" className={styles.textBtn} onClick={() => inputRef.current?.click()}>
                    Add another file
                </button>
            ) : null}
        </div>
    );
}

function SheetStep({ sheets, onToggleSheet, onPurpose, onPreviewSheet, onChooseHeaderRow }) {
    const trackerSuggested = sheets.filter(sheet => sheet.sheet_role === 'tracker_data').length;
    const selectedForImport = sheets.filter(sheet => sheet.selected).length;
    return (
        <div>
            <p className={styles.sectionCopy}>We found {sheets.length} worksheets. Lens pre-selects tabs that look like tracker data and leaves instructions, dashboards, and IT exports off by default.</p>
            <p className={styles.metaLine}>
                {selectedForImport} selected for import
                {trackerSuggested ? ` · ${trackerSuggested} tracker tab${trackerSuggested === 1 ? '' : 's'} suggested` : ''}. Confirm purpose and header row where flagged.
            </p>
            {sheets.map(sheet => {
                const review = sheetReviewState(sheet) === 'review';
                const roleLabel = sheetRoleLabel(sheet);
                return (
                    <article key={sheet.id} className={review ? `${styles.sheetCard} ${styles.sheetCardWarn}` : styles.sheetCard}>
                        <div className={styles.sheetRow}>
                            <label className={styles.sheetCheck}>
                                <input className={styles.check} type="checkbox" checked={Boolean(sheet.selected)} aria-label={`Include ${sheet.name}`} onChange={() => onToggleSheet(sheet.id)} />
                            </label>
                            <div className={styles.sheetMain}>
                                <p className={styles.sheetName}>{sheet.name}</p>
                                <p className={styles.sheetMeta}>
                                    {sheet.filename} · {sheet.row_count} data rows · {sheet.column_count} columns
                                </p>
                                <div className={styles.sheetActions}>
                                    <button type="button" className={styles.textBtn} onClick={() => onPreviewSheet(sheet.id)}>
                                        Preview
                                    </button>
                                    {sheet.needs_header_review && !sheet.header_confirmed ? (
                                        <button type="button" className={styles.textBtn} onClick={() => onChooseHeaderRow(sheet.id)}>
                                            Choose header row
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                            <select
                                className={styles.purposeSelect}
                                aria-label={`Purpose for ${sheet.name}`}
                                value={sheet.kind || 'other'}
                                onChange={event => onPurpose(sheet.id, event.target.value)}>
                                {SHEET_PURPOSES.map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                            <span className={review ? styles.badgeWarn : styles.badge}>{review ? 'Needs review' : roleLabel || 'Suggested'}</span>
                        </div>
                        <p className={styles.sheetHelp}>{sheetSelectionHelp(sheet)}</p>
                    </article>
                );
            })}
        </div>
    );
}

function MatchStep({
    sheets,
    matchSheetId,
    statusRules,
    statusSamples,
    dateSamples,
    dateConvention,
    aiMappingEnabled,
    aiBusy,
    onAiMapping,
    onMatchSheet,
    onMapColumn,
    onStatusRule,
    onDateConvention,
    statusRef,
    dateRef,
}) {
    const active = sheets.find(sheet => sheet.id === matchSheetId) || sheets[0];
    const summary = matchSummary(active, statusRules, statusSamples, dateConvention, dateSamples);
    const statusTarget = (active?.columns || []).some(column => columnTarget(column) === 'status');
    const dateTarget = (active?.columns || []).some(column => columnTarget(column) === 'target_date') && sheetHasAmbiguousDates(active, dateSamples);
    const sheetStatusRules = statusRulesForSheet(active, statusRules, statusSamples);

    return (
        <div>
            <div className={styles.matchIntroRow}>
                <p className={styles.sectionCopy}>We&apos;ve suggested how your columns fit into Lens. Adjust anything that does not look right.</p>
                {aiMappingEnabled ? (
                    <button type="button" className={styles.ghostBtn} disabled={aiBusy} onClick={onAiMapping}>
                        {aiBusy ? 'Suggesting…' : 'Improve with AI'}
                    </button>
                ) : null}
            </div>
            <div className={styles.matchLayout}>
                <div className={styles.matchSide}>
                    {sheets.map(sheet => {
                        const label = sheetMatchLabel(sheet, statusRules, statusSamples, dateConvention, dateSamples);
                        const on = active?.id === sheet.id;
                        return (
                            <button type="button" key={sheet.id} className={on ? styles.chipOn : styles.chip} onClick={() => onMatchSheet(sheet.id)}>
                                {sheet.name}
                                <span className={styles.chipMeta}>
                                    {sheet.row_count} rows · {label}
                                </span>
                            </button>
                        );
                    })}
                </div>
                <div className={styles.matchMain}>
                    <div className={styles.statPills}>
                        <span>{summary.matched} matched automatically</span>
                        <span>{summary.review} need review</span>
                        <span>{summary.custom} kept as a custom column</span>
                    </div>
                    <div className={styles.mapTable}>
                        <div className={`${styles.mapRow} ${styles.mapHead}`}>
                            <span>Your column</span>
                            <span>Use in Lens as</span>
                            <span>Check</span>
                        </div>
                        {(active?.columns || []).map(column => {
                            const target = columnTarget(column);
                            const state = mappingState(column, active, statusRules, statusSamples, dateConvention, dateSamples);
                            return (
                                <div key={column.id} className={state === 'review' ? `${styles.mapRow} ${styles.mapRowWarn}` : styles.mapRow}>
                                    <div>
                                        <p className={styles.mapColumn}>{column.header || `Column ${column.index + 1}`}</p>
                                        {column.suggestion?.suggested_by === 'ai' ? <span className={styles.aiBadge}>AI suggested</span> : null}
                                        <p className={styles.mapSample}>{(column.samples || []).slice(0, 2).join(' · ') || '—'}</p>
                                        {state === 'custom' ? <p className={styles.mapSample}>We&apos;ll keep this column exactly as it is.</p> : null}
                                    </div>
                                    <select
                                        className={styles.select}
                                        aria-label={`Map ${column.header || column.index}`}
                                        value={target}
                                        onChange={event => onMapColumn(active.id, column.index, event.target.value)}>
                                        {FIELD_TARGETS.map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {value === 'custom' ? 'Custom field (keep original name)' : label}
                                            </option>
                                        ))}
                                    </select>
                                    <div>
                                        <p className={state === 'matched' ? styles.matched : state === 'review' ? styles.review : styles.custom}>
                                            {state === 'matched' ? 'Matched' : state === 'review' ? 'Review needed' : 'Custom'}
                                        </p>
                                        {state === 'review' && target === 'status' ? (
                                            <button type="button" className={styles.textBtn} onClick={() => statusRef.current?.scrollIntoView({ behavior: 'smooth' })}>
                                                Review status values
                                            </button>
                                        ) : null}
                                        {state === 'review' && target === 'target_date' ? (
                                            <button type="button" className={styles.textBtn} onClick={() => dateRef.current?.scrollIntoView({ behavior: 'smooth' })}>
                                                Review date format
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {statusTarget || dateTarget ? (
                <div className={styles.advancedBlock} ref={statusRef}>
                    {statusTarget ? (
                        <>
                            <h3 className={styles.sectionTitle}>Status values</h3>
                            <p className={styles.sectionCopy}>Confirm how values from this worksheet map to Lens statuses.</p>
                            {sheetStatusRules.map(([raw, rule]) => (
                                <label key={raw} className={styles.field}>
                                    <span className={styles.fieldLabel}>{raw}</span>
                                    <select className={styles.select} aria-label={`Status for ${raw}`} value={rule.confirmed ? rule.canonical : ''} onChange={event => onStatusRule(raw, event.target.value)}>
                                        <option value="">Leave unknown</option>
                                        <option value="NOT_STARTED">Not started</option>
                                        <option value="IN_PROGRESS">In progress</option>
                                        <option value="BLOCKED">Blocked</option>
                                        <option value="AT_RISK">At risk</option>
                                        <option value="ON_HOLD">On hold</option>
                                        <option value="COMPLETE">Complete</option>
                                        <option value="NOT_APPLICABLE">Not applicable</option>
                                    </select>
                                </label>
                            ))}
                        </>
                    ) : null}
                    {dateTarget ? (
                        <label className={styles.field} ref={dateRef}>
                            <span className={styles.fieldLabel}>Ambiguous dates such as 05/06/2026</span>
                            <select className={styles.select} aria-label="Date format" value={dateConvention || ''} onChange={event => onDateConvention(event.target.value)}>
                                <option value="">Do not guess</option>
                                <option value="DMY">Day / month / year</option>
                                <option value="MDY">Month / day / year</option>
                            </select>
                        </label>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function sheetOverviewLine(sheet) {
    if (!sheet.selected) return 'Not imported';
    if (sheet.kind === 'supporting') return 'Excluded from overview counts';
    return 'Contributes where mapped';
}

function reviewOverviewNotes(preview, held) {
    const notes = [];
    const blocking = preview?.blocking || [];
    if (blocking.some(issue => issue.code === 'header_unconfirmed')) {
        notes.push('Confirm the header row on worksheets flagged during import before counts are final.');
    }
    if (blocking.some(issue => issue.code === 'purpose_unconfirmed')) {
        notes.push('Confirm the purpose for each selected worksheet.');
    }
    if (held > 0) {
        notes.push(`${held} item${held === 1 ? '' : 's'} will be held for review until mappings are confirmed.`);
    }
    return notes;
}

function ReviewStep({ wizard, sheets, onReviewed, onGoToMatch }) {
    const [openSheets, setOpenSheets] = React.useState(() => new Set(sheets.map(sheet => sheet.id)));
    React.useEffect(() => {
        setOpenSheets(new Set(sheets.map(sheet => sheet.id)));
    }, [sheets]);
    const counts = wizard.preview?.counts || {};
    const held = counts.HOLD_FOR_REVIEW || 0;
    const previewRows = wizard.preview?.rows || [];
    const duplicateCandidates = previewRows.filter(row => (row.warnings || []).includes('POTENTIAL_DUPLICATE')).length;
    const customColumns = sheets.reduce((total, sheet) => total + (sheet.columns || []).filter(column => columnTarget(column) === 'custom').length, 0);
    const supportingRows = sheets.filter(sheet => sheet.kind === 'supporting' && sheet.selected).reduce((total, sheet) => total + (sheet.row_count || 0), 0);
    const overviewNotes = reviewOverviewNotes(wizard.preview, held);
    const showFixMappings = overviewNotes.length > 0;

    const toggleSheet = sheetId => {
        setOpenSheets(current => {
            const next = new Set(current);
            if (next.has(sheetId)) next.delete(sheetId);
            else next.add(sheetId);
            return next;
        });
    };

    return (
        <div>
            <h3 className={styles.reviewIntroTitle}>Here&apos;s what Lens will bring in.</h3>
            <p className={styles.sectionCopy}>Nothing changes until you confirm.</p>
            <div className={styles.countBoard}>
                <ReviewMetric label="New items" value={counts.CREATE || 0} />
                <ReviewMetric label="Updates" value={counts.UPDATE || 0} />
                <ReviewMetric label="Unchanged" value={counts.UNCHANGED || 0} />
                <ReviewMetric label="Needs review" value={held} highlight={held > 0} />
                <ReviewMetric label="Duplicate candidates" value={duplicateCandidates} />
                <ReviewMetric label="Custom columns preserved" value={customColumns} />
                <ReviewMetric label="Supporting rows" value={supportingRows} />
            </div>
            <div className={styles.reviewSheetBoard}>
                {sheets.map(sheet => {
                    const open = openSheets.has(sheet.id);
                    const fieldMatches = sheetMatchLabel(sheet, wizard.statusRules, wizard.statusSamples, wizard.dateConvention, wizard.dateSamples);
                    const fieldLabel = fieldMatches === 'Review needed' ? 'Needs review' : 'Good';
                    return (
                        <div key={sheet.id} className={styles.reviewSheetBlock}>
                            <button type="button" className={styles.reviewSheetHead} onClick={() => toggleSheet(sheet.id)} aria-expanded={open}>
                                <span className={styles.reviewSheetChevron} aria-hidden="true">
                                    {open ? '▾' : '▸'}
                                </span>
                                <span className={styles.reviewSheetTitle}>
                                    {sheet.name} · {sheet.row_count} rows · {purposeLabel(sheet.kind)}
                                </span>
                            </button>
                            {open ? (
                                <div className={styles.reviewSheetMeta}>
                                    <span>Field matches: {fieldLabel}</span>
                                    <span>Overview: {sheetOverviewLine(sheet)}</span>
                                    <span>Source identity preserved</span>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            <div className={styles.overviewCallout}>
                <p className={styles.overviewCalloutTitle}>What will appear in Overview?</p>
                <p className={styles.overviewCalloutCopy}>
                    Tracked item counts, confirmed statuses, business functions, mapped target dates and explicit blockers. Supporting rows are excluded.
                </p>
                {overviewNotes.map(note => (
                    <p key={note} className={styles.overviewCalloutWarn}>
                        {note}
                    </p>
                ))}
                {showFixMappings ? (
                    <button type="button" className={styles.textBtn} onClick={onGoToMatch}>
                        Fix mappings
                    </button>
                ) : null}
            </div>
            <label className={styles.checkLine}>
                <input className={styles.check} type="checkbox" checked={Boolean(wizard.reviewed)} onChange={event => onReviewed(event.target.checked)} />
                I&apos;ve reviewed the proposed additions and updates.
            </label>
        </div>
    );
}

function ReviewMetric({ label, value, highlight }) {
    return (
        <div className={styles.countBoardCell}>
            <p className={styles.countLabel}>{label}</p>
            <p className={highlight ? styles.countBoardValueWarn : styles.countBoardValue}>{value}</p>
        </div>
    );
}

function FinishStep({ wizard, result, onOpenOverview, onOpenTracker, onViewDetails }) {
    const counts = result?.result || result?.summary || result || {};
    const selectedSheets = (wizard.files || []).flatMap(file => file.worksheets || []).filter(sheet => sheet.selected).length;
    const created = counts.created || 0;
    const updated = counts.updated || 0;
    const held = counts.held_for_review || 0;
    const imported = created + updated + (counts.unchanged || 0);

    return (
        <div className={styles.finish}>
            <div className={styles.checkCircle} aria-hidden="true">
                ✓
            </div>
            <h3 className={styles.finishTitle}>Your tracker is ready.</h3>
            <p className={styles.finishCopy}>
                {imported || created + updated} records imported · {selectedSheets || counts.source_worksheets_included || 0} selected sheets · {held} item{held === 1 ? '' : 's'} held for review.
            </p>
            <div className={styles.finishActions}>
                <button type="button" className={styles.primaryBtn} onClick={onOpenOverview}>
                    Open Overview
                </button>
                <button type="button" className={styles.ghostBtn} onClick={onOpenTracker}>
                    View Tracker
                </button>
                <button type="button" className={styles.ghostBtn} onClick={onViewDetails}>
                    View import details
                </button>
            </div>
        </div>
    );
}

function Count({ label, value }) {
    return (
        <div className={styles.countCard}>
            <p className={styles.countLabel}>{label}</p>
            <p className={styles.countValue}>{value}</p>
        </div>
    );
}
